import { RequestOptions } from '@/interfaces/IHttpRequest';
import { CrmGroup, Group, LeadNotesResponse } from '@/interfaces/IColumn';
import {
	CreateGroupData,
	UpdateGroupData,
	UpdateLeadStatusData,
	UpdateLeadData,
	UpdateLeadNotesData,
	DeleteNoteData,
} from '@/interfaces/ICrmApisInterface';
import { toast } from 'react-toastify';
import apiClient from '@/utils/apiClient';

const createRequestOptions = (
	method: 'GET' | 'PUT' | 'POST' | 'DELETE',
	endpoint: string,
	data?: any,
	headers?: HeadersInit,
): RequestOptions => ({
	method,
	endPoint: endpoint,
	body: data,
	headers: {
		'Content-Type': data instanceof FormData ? 'multipart/form-data' : 'application/json',
		...headers,
	},
});

export const fetchGroups = async (username: string): Promise<{ crmGroups: CrmGroup[] }> => {
	if (!username) {
		//@ts-expect-error ignore for now
		return { groups: [] };
	}
	const response = await apiClient.request(
		createRequestOptions('GET', `/api/crm2/groups?clientID=${username}`),
	);
	return response;
};

export const createGroup = async (groupData: CreateGroupData): Promise<Group> => {
	const response = await apiClient.request(
		createRequestOptions('POST', '/api/crm/groups', groupData),
	);
	toast.success('Group created successfully!');
	return response;
};

export const updateGroup = async (data: UpdateGroupData): Promise<Group> => {
	const { username, groupId, ...updateData } = data;
	const response = await apiClient.request(
		createRequestOptions('PUT', `/api/crm/groups/${username}/${groupId}`, updateData),
	);
	toast.success('Group updated successfully!');
	return response;
};

export const deleteGroup = async ({
	username,
	groupId,
}: {
	username: string;
	groupId: string;
}): Promise<any> => {
	const response = await apiClient.request(
		createRequestOptions('DELETE', `/api/crm/groups/${username}/${groupId}`),
	);

	if (!response) {
		throw new Error('Empty response from server');
	}

	if (typeof response !== 'object') {
		throw new Error('Invalid response from server');
	}

	return response;
};

export const updateLeadStatus = async (data: UpdateLeadStatusData): Promise<any> => {
	const response = await apiClient.request(
		createRequestOptions('PUT', '/api/crm/leads/updateStatus', {
			username: data.username,
			lead_id: data.lead_id,
			current_status: data.current_status,
			phone_number: data.phone_number,
		}),
	);

	return response;
};

export const updateLeadStatusOnKanban = async (data: UpdateLeadStatusData): Promise<any> => {
	const response = await apiClient.request(
		createRequestOptions('PUT', '/api/crm/leads/updateStatusOnKanban/', {
			//@ts-expect-error ignore for now
			newGroupId: data.newGroupId,
			username: data.username,
			lead_id: data.lead_id,
			phone_number: data.phone_number,
		}),
	);

	return response;
};

export const updateLeadApi = async (data: UpdateLeadData): Promise<any> => {
	const response = await apiClient.request(
		createRequestOptions('PUT', '/api/crm/leads/updateLead', data),
	);

	return response;
};

export const updateLeadNotesApi = async (data: UpdateLeadNotesData): Promise<any> => {
	const response = await apiClient.request(
		createRequestOptions('PUT', `/api/crm/leads/notes/${data.username}/${data.lead_id}`, data),
	);

	return response;
};

export const deleteNoteApi = async (data: DeleteNoteData): Promise<any> => {
	const response = await apiClient.request(
		createRequestOptions('DELETE', `/api/crm/leads/notes/${data.username}/${data.lead_id}`, data),
	);

	return response;
};

export const getSingleLeadNotesApi = async (
	username: string,
	lead_id: string,
): Promise<LeadNotesResponse> => {
	const response = await apiClient.request(
		createRequestOptions('GET', `/api/crm/leads/notes/${username}/${lead_id}`),
	);

	return response;
};

export const fetchWorkflows = async (username: string): Promise<any> => {
	const response = await apiClient.request(
		createRequestOptions('GET', `/api/clients/workflows/${username}`),
	);

	return response.data;
};

export const getContacts = async (username: string): Promise<any> => {
	const response = await apiClient.request(
		createRequestOptions('POST', '/get-contacts', { username }),
	);

	return response;
};

export const updateContactName = async (lead_id: string, name: string) => {
	const response = await apiClient.request(
		createRequestOptions('POST', '/update-contact', {
			contact: { [lead_id]: name },
			username: 'YOUR_USERNAME_HERE', // Replace with actual username logic
		}),
	);

	return response;
};
